import { useEffect, useMemo, useState } from 'react'
import { useGetLiveData } from '@/services/hooks/relay/dynamic'

type UseBootstrappedLiveListSelectionProps = {
  infoTypeId: string
  orgId: string
  orgCredentialTypeId: string
  enabled: boolean
  currentId?: string | null
  searchValue: string
  limit: string
  resetKey: string
}

/**
 * Resolves a live-list selection with a one-time bootstrap fetch for the initial ID,
 * then continues with normal searchable/paginated list behavior (unfiltered until
 * search text exists) without re-bootstrapping when the selected value changes.
 */
export default function useBootstrappedLiveListSelection<T>({
  infoTypeId,
  orgId,
  orgCredentialTypeId,
  enabled,
  currentId,
  searchValue,
  limit,
  resetKey,
}: UseBootstrappedLiveListSelectionProps) {
  const [initialId, setInitialId] = useState<string | null | undefined>(
    undefined,
  )
  const [isBootstrapComplete, setIsBootstrapComplete] = useState(false)

  // Reset one-time bootstrap whenever the backing entity/context changes.
  useEffect(() => {
    setInitialId(undefined)
    setIsBootstrapComplete(false)
  }, [resetKey])

  // Capture only the first resolved ID; later user edits should not re-bootstrap.
  useEffect(() => {
    if (isBootstrapComplete) return
    if (currentId === undefined) return

    const resolvedId = currentId || null
    setInitialId(resolvedId)

    if (!resolvedId) {
      setIsBootstrapComplete(true)
    }
  }, [currentId, isBootstrapComplete])

  const { allData: initialOptionData, isPending: isPendingInitialOption } =
    useGetLiveData<T>({
      infoTypeId,
      orgId,
      orgCredentialTypeId,
      filters: {
        id: initialId || '',
      },
      enabled: enabled && !!initialId && !isBootstrapComplete,
    })

  const initialOption = initialOptionData[0]

  useEffect(() => {
    if (!isBootstrapComplete && !!initialId && !isPendingInitialOption) {
      setIsBootstrapComplete(true)
    }
  }, [initialId, isBootstrapComplete, isPendingInitialOption])

  const { allData, hasNextPage, fetchNextPage, isPending, isFetching } =
    useGetLiveData<T>({
      infoTypeId,
      orgId,
      orgCredentialTypeId,
      filters: {
        // Only filter when user has typed a search value.
        ...(searchValue ? { query: searchValue } : {}),
        limit,
      },
      enabled:
        enabled &&
        currentId !== undefined &&
        (!initialId || isBootstrapComplete),
    })

  const options = useMemo(() => {
    if (!initialOption) return allData
    if (currentId !== initialOption.entity_id) return allData
    if (
      allData.some((option) => option.entity_id === initialOption.entity_id)
    ) {
      return allData
    }
    return [initialOption, ...allData]
  }, [allData, initialOption, currentId])

  const selectedOption = useMemo(
    () => options.find((option) => option.entity_id === currentId),
    [options, currentId],
  )

  return {
    options,
    selectedOption,
    hasNextPage,
    fetchNextPage,
    isFetching,
    isLoading:
      (isPendingInitialOption && !isBootstrapComplete) ||
      isPending ||
      (!!currentId && !selectedOption),
  }
}
